import type { ProjectAnalysisInput } from "@/types/analysis/ProjectAnalysisInput";
import { getCo2FieldsFromScenarioModel, getModel } from "@/types/analysis/ProjectAnalysisInput";
import "@/types/Object.extensions";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import { sumWithNullsAgg } from "@components/DataGrid/utils/aggregation";
import useRowSelectionModel from "@features/analysis/hooks/useSelectionModel";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import { useTheme } from "@mui/material";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridGroupNode,
  GridRowId,
  GridRowSelectionModel,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import _ from "lodash";
import { forwardRef, MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { getColumns, getFilteredColumns, getGroupingColumnDef } from "./ResultsGrid.columns";

export interface ResultGridProps {
  apiRef: MutableRefObject<GridApiPremium>;
  /** ids: row IDs; rowSelectionModel: same selection for parent to use when syncing charts (avoids ref timing) */
  onSelect: () => void;
  /** NodeDto-based analysis input; when null, grid shows empty rows */
  analysisInput?: ProjectAnalysisInput | null;
  /** Loading state for the grid (e.g. from useProjectAnalysisData.isFetching) */
  loading?: boolean;
  /** Optional ref for parent to read current row selection (e.g. to sync charts with grid selection) */
  selectionModelRef?: MutableRefObject<GridRowId[] | undefined>;
}

const gridStorageKey = "results-grid";

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  aggregation: {
    model: {
      a1A2A3: "sum",
      a4A5: "sum",
      a: "sum",
      b1: "sum",
      b2B5: "sum",
      biogenic: "sum",
      b: "sum",
      c: "sum",
      d: "sum",
      totalCo2: "sum",
      percentRebar: "avg",
      percentAssigned: "avg",
    },
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
  },
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const slots = {
  toolbar: GridToolbarCustom,
};

const slotProps = {
  toolbar: {
    showQuickFilter: false,
    showSearchFilter: false,
    printOptions: { disableToolbarButton: true },
    csvOptions: { disableToolbarButton: true },
    excelOptions: { disableToolbarButton: true },
    headerFiltersOptions: { disableToolbarButton: true },
  },
};

const getRowId = (row: any) => {
  try {
    return row.key;
  } catch (error) {
    console.error("getRowId error: ", error);
  }
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

const co2StageFields = ["a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "b6", "b7", "biogenic", "c1", "c2", "c3", "c4", "d"] as const;

const getFiniteNumber = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  const parsed = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

const ResultsGridRef = forwardRef<any, PropsWithChildren<ResultGridProps>>((props, ref) => {
  const { apiRef, analysisInput, loading = false, selectionModelRef, onSelect } = props;
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const theme = useTheme();
  const [key, setKey] = useState(0); // increment key to force re-initialize of the grid

  const { handelRowSelectionModelChange, selectionModel } = useRowSelectionModel(apiRef);

  useEffect(() => {
    if (selectionModelRef) {
      selectionModelRef.current = selectionModel;
    }
  }, [selectionModel, selectionModelRef]);

  const handleRowSelectionChange = async (selection: GridRowSelectionModel) => {
    console.log("onSelectionChange before: ", selection);

    const { models } = await handelRowSelectionModelChange(selection as GridRowId[]);

    console.log("onSelectionChange after: ", selection);

    _.delay(async () => {
      for (const item of models) {
        await selectionManager.selectModels(item.data.scenarioId, item.data.model.apsViewId, true);
      }
      onSelect();
    }, 100);
  };

  useEffect(() => {
    const newRows: any[] = [];

    if (analysisInput?.scenarioEntries) {
      analysisInput.scenarioEntries.forEach((scenarioEntry, index) => {
        const scenarioId = scenarioEntry.scenario.scenarioId as string;
        const scenario = scenarioEntry.scenario;
        let scenarioPercentAssignedSum = 0;
        let scenarioPercentAssignedCount = 0;
        const modelRows: any[] = [];

        scenarioEntry.modelEntries.forEach((modelEntry, modelIndex) => {
          const sm = modelEntry.scenarioModel;
          const modelCo2ByField = getCo2FieldsFromScenarioModel(sm);
          const totalCo2 = getFiniteNumber(sm.totalCo2) ?? getFiniteNumber(modelCo2ByField.totalCo2) ?? 0;
          const percentAssigned = getFiniteNumber(sm.percentAssigned) ?? 0;

          scenarioPercentAssignedSum += percentAssigned;
          scenarioPercentAssignedCount += 1;

          const entryModel = getModel(modelEntry);
          const scenarioModelId = sm.scenarioModelId ?? `${scenarioId}-${entryModel.modelId}`;

          modelRows.push({
            index: modelIndex,
            key: scenarioModelId,
            path: [scenarioId, scenarioModelId],
            type: "model",
            data: {
              scenarioId,
              scenarioModelId,
              model: entryModel,
              tag: `S${index + 1}-M${modelIndex + 1}`,
              name: entryModel?.modelName,
              ..._.pick(modelCo2ByField, co2StageFields),
              totalCo2,
              percentAssigned,
            },
          });
        });

        const scenarioPercentAssigned = scenarioPercentAssignedCount > 0 ? scenarioPercentAssignedSum / scenarioPercentAssignedCount : 0;
        const finalScenarioTotalCo2 = getFiniteNumber((scenario as { totalCo2?: number | string }).totalCo2) ?? 0;
        const scenarioPercentFromApi =
          typeof (scenario as { getPercentAssigned?: () => number }).getPercentAssigned === "function"
            ? (scenario as { getPercentAssigned: () => number }).getPercentAssigned()
            : undefined;
        const finalScenarioPercentAssigned = scenarioPercentFromApi != null && _.isFinite(scenarioPercentFromApi) ? scenarioPercentFromApi : scenarioPercentAssigned;
        const scenarioData = scenario as unknown as Record<string, unknown>;
        let percentOfEmissionReductionTarget = (finalScenarioTotalCo2 / (selectionManager.selectedProject?.projectEmissionReductionTarget || 0)) * 100;
        percentOfEmissionReductionTarget = _.isFinite(percentOfEmissionReductionTarget) ? percentOfEmissionReductionTarget : 0;

        newRows.push({
          index,
          key: scenarioId,
          path: [scenarioId],
          type: "scenario",
          data: {
            ...scenario,
            scenarioId,
            tag: `S${index + 1}`,
            ...Object.fromEntries(
              co2StageFields.map((field) => [field, getFiniteNumber(scenarioData[field])]),
            ),
            totalCo2: finalScenarioTotalCo2,
            percentOfEmissionReductionTarget,
            percentAssigned: finalScenarioPercentAssigned,
          },
        });
        newRows.push(...modelRows);
      });
    }

    console.log("rows: ", newRows);
    apiRef.current?.updateRows?.(newRows);
  }, [apiRef, analysisInput, selectionManager.selectedProject?.projectEmissionReductionTarget]);

  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("results-grid.columns.grouping")), [apiRef]);

  const columns = useMemo(() => getColumns(apiRef, appStore, theme), [apiRef, appStore, theme]);
  const { filteredColumns, columnVisibilityModel: initialVisibilityModel } = useMemo(() => getFilteredColumns(columns, appStore), [columns, appStore]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(initialVisibilityModel);

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      columns: {
        columnVisibilityModel,
      },
      headerFilters: true, // this needs to be sync'd with DataGridContext.headerFilters
    }),
    [columnVisibilityModel],
  );

  const getAggregatePosition = useCallback((groupNode: GridGroupNode) => {
    return groupNode.depth === -1 ? null : "inline";
  }, []);

  const aggregationFunctions = useMemo(() => {
    return {
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: sumWithNullsAgg,
    };
  }, []);

  console.log(initialState);
  console.log(filteredColumns);
  console.log(apiRef.current?.state?.rows);

  return (
    <DataGrid
      key={key}
      ref={ref}
      apiRef={apiRef}
      treeData
      autosizeColumns
      autosizeTreeDataGroupingColumns
      checkboxSelection
      disableRowSelectionOnClick
      getTreeDataPath={getTreeDataPath}
      columns={filteredColumns}
      rowSelectionPropagation={rowSelectionPropagation}
      initialState={initialState}
      groupingColDef={groupingColumnDef}
      getRowId={getRowId}
      slots={slots}
      slotProps={slotProps}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={handleRowSelectionChange}
      loading={loading}
      aggregationFunctions={aggregationFunctions}
      getAggregationPosition={getAggregatePosition}
      hideFooter
      disableColumnFilter
      storageKey={gridStorageKey}
    />
  );
});

const ResultsGrid = ResultsGridRef;
export default ResultsGrid;
